import React, { useState, useEffect } from 'react';
import { RiFolderAddLine, RiEditLine } from 'react-icons/ri';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
  mode: 'create' | 'rename';
  initialName?: string;
  parentFolderName?: string;
  loading?: boolean;
}

const FolderModal: React.FC<FolderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialName = '',
  parentFolderName,
  loading = false,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState(mode);
  const [currentParentFolderName, setCurrentParentFolderName] =
    useState(parentFolderName);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError(null);
      setCurrentMode(mode); // Capture mode when modal opens
      setCurrentParentFolderName(parentFolderName); // Capture parent folder name
    }
  }, [isOpen, initialName, mode, parentFolderName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Folder name is required');
      return;
    }

    if (trimmedName.length > 100) {
      setError('Folder name is too long (max 100 characters)');
      return;
    }

    setError(null);
    onSubmit(trimmedName);
  };

  const title =
    currentMode === 'create' ? 'Create New Folder' : 'Rename Folder';
  const Icon = currentMode === 'create' ? RiFolderAddLine : RiEditLine;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>

      <form onSubmit={handleSubmit}>
        {currentParentFolderName && currentParentFolderName !== 'Root' && (
          <p className="text-sm text-gray-600 mb-3">
            {currentMode === 'create'
              ? `Creating subfolder in: ${currentParentFolderName}`
              : `Renaming folder: ${currentParentFolderName}`}
          </p>
        )}

        <div className="mb-4">
          <label
            htmlFor="folder-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Folder Name
          </label>
          <Input
            id="folder-name"
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value);
              setError(null);
            }}
            placeholder="Enter folder name"
            disabled={loading}
            autoFocus
            className="w-full"
          />
          {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !name.trim()}
          >
            {loading
              ? 'Saving...'
              : currentMode === 'create'
                ? 'Create'
                : 'Rename'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FolderModal;
